import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  LayoutDashboard,
  Bot,
  MessageSquare,
  Settings,
  LogOut,
  ChevronDown,
  Database,
  Moon,
  Sun,
  Shield,
  ContactRound,
  Briefcase,
  Clock3
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agent-hub-177/artifacts/isc5pm97_Apollo%20Wide%20Logo.png";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/agents', label: 'Agent Dashboard', icon: Bot },
    { path: '/agent-employees', label: 'Agent Employees', icon: Briefcase },
    { path: '/cron-jobs', label: 'Cron Jobs', icon: Clock3 },
    { path: '/knowledge-base', label: 'Knowledge Base', icon: Database },
    { path: '/conversations', label: 'Conversations', icon: MessageSquare },
    { path: '/contacts', label: 'Contacts', icon: ContactRound },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  // Add admin link for admin users
  if (user?.role === 'admin') {
    navItems.push({ path: '/admin', label: 'Admin Panel', icon: Shield });
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-64 min-h-screen bg-card border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Apollo" 
              className="h-8 w-auto"
            />
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive(item.path)
                  ? 'apollo-brand-surface text-foreground font-medium border border-primary/25 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className={`w-4 h-4 ${isActive(item.path) ? 'text-primary' : ''}`} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            data-testid="theme-toggle"
          >
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              <span>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
            </div>
          </button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="flex items-center gap-3">
                  <div className="apollo-brand-avatar w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="truncate max-w-[120px]">{user?.name || 'User'}</span>
                </div>
                <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default Layout;
